import React, { memo, useMemo } from 'react';
import {
  WidgetProps,
  I18nextProvider as I18nextProviderICU,
} from '@wix/yoshi-flow-editor';

import {
  getBaseLanguage,
  getCurrentUser,
} from '@common/store/base-props-slice';
import { getCurrentError } from '@common/store/errors-slice';
import { getTranslations } from '@common/store/translations-slice';
import {
  BiLoggerContextProvider,
  PermissionsContextProvider,
} from '@common/providers';
import { getI18nConfigICU } from '@common/utils';
import { ErrorBoundary } from '@common/components';
import { ControllerProps } from '../controller';
import { MemberComments } from './pages';

const DEPRECATION_TRANSLATIONS: Record<string, { title: string; description: string }> = {
  en: { title: 'Wix Forum is no longer available', description: 'This application has been discontinued. If you need community app use Wix Groups.' },
  ar: { title: 'منتدى Wix لم يعد متاحًا', description: 'تم إيقاف هذا التطبيق. إذا كنت بحاجة إلى تطبيق مجتمع، استخدم Wix Groups.' },
  bg: { title: 'Wix Forum вече не е наличен', description: 'Това приложение е преустановено. Ако имате нужда от приложение за общност, използвайте Wix Groups.' },
  ca: { title: 'Wix Forum ja no està disponible', description: 'Aquesta aplicació ha estat descontinuada. Si necessiteu una aplicació de comunitat, utilitzeu Wix Groups.' },
  cs: { title: 'Wix Forum již není k dispozici', description: 'Tato aplikace byla ukončena. Pokud potřebujete komunitní aplikaci, použijte Wix Groups.' },
  da: { title: 'Wix Forum er ikke længere tilgængeligt', description: 'Denne applikation er blevet afviklet. Hvis du har brug for en fællesskabsapp, skal du bruge Wix Groups.' },
  de: { title: 'Wix Forum ist nicht mehr verfügbar', description: 'Diese Anwendung wurde eingestellt. Wenn Sie eine Community-App benötigen, verwenden Sie Wix Groups.' },
  el: { title: 'Το Wix Forum δεν είναι πλέον διαθέσιμο', description: 'Αυτή η εφαρμογή έχει διακοπεί. Αν χρειάζεστε εφαρμογή κοινότητας, χρησιμοποιήστε το Wix Groups.' },
  es: { title: 'Wix Forum ya no está disponible', description: 'Esta aplicación ha sido descontinuada. Si necesitas una app de comunidad, usa Wix Groups.' },
  fi: { title: 'Wix Forum ei ole enää käytettävissä', description: 'Tämä sovellus on poistettu käytöstä. Jos tarvitset yhteisösovelluksen, käytä Wix Groupsia.' },
  fr: { title: 'Wix Forum n\'est plus disponible', description: 'Cette application a été abandonnée. Si vous avez besoin d\'une application communautaire, utilisez Wix Groups.' },
  he: { title: 'Wix Forum אינו זמין יותר', description: 'אפליקציה זו הופסקה. אם אתם צריכים אפליקציית קהילה, השתמשו ב-Wix Groups.' },
  hi: { title: 'Wix Forum अब उपलब्ध नहीं है', description: 'यह एप्लिकेशन बंद कर दिया गया है। अगर आपको कम्युनिटी ऐप चाहिए तो Wix Groups का उपयोग करें।' },
  hr: { title: 'Wix Forum više nije dostupan', description: 'Ova aplikacija je ukinuta. Ako vam je potrebna aplikacija za zajednicu, koristite Wix Groups.' },
  hu: { title: 'A Wix Forum már nem érhető el', description: 'Ez az alkalmazás megszűnt. Ha közösségi alkalmazásra van szüksége, használja a Wix Groupsot.' },
  id: { title: 'Wix Forum tidak lagi tersedia', description: 'Aplikasi ini telah dihentikan. Jika Anda membutuhkan aplikasi komunitas, gunakan Wix Groups.' },
  it: { title: 'Wix Forum non è più disponibile', description: 'Questa applicazione è stata dismessa. Se hai bisogno di un\'app per la community, usa Wix Groups.' },
  ja: { title: 'Wix Forumはご利用いただけなくなりました', description: 'このアプリケーションは廃止されました。コミュニティアプリが必要な場合は、Wix Groupsをご利用ください。' },
  ko: { title: 'Wix Forum은 더 이상 사용할 수 없습니다', description: '이 애플리케이션은 중단되었습니다. 커뮤니티 앱이 필요하시면 Wix Groups를 이용해 주세요.' },
  lt: { title: 'Wix Forum nebėra pasiekiamas', description: 'Ši programa buvo nutraukta. Jei jums reikia bendruomenės programos, naudokite Wix Groups.' },
  lv: { title: 'Wix Forum vairs nav pieejams', description: 'Šī lietotne ir pārtraukta. Ja jums nepieciešama kopienas lietotne, izmantojiet Wix Groups.' },
  ms: { title: 'Wix Forum tidak lagi tersedia', description: 'Aplikasi ini telah dihentikan. Jika anda memerlukan aplikasi komuniti, gunakan Wix Groups.' },
  nl: { title: 'Wix Forum is niet meer beschikbaar', description: 'Deze applicatie is stopgezet. Als u een community-app nodig heeft, gebruik dan Wix Groups.' },
  no: { title: 'Wix Forum er ikke lenger tilgjengelig', description: 'Denne applikasjonen er avviklet. Hvis du trenger en fellesskapsapp, bruk Wix Groups.' },
  pl: { title: 'Wix Forum nie jest już dostępne', description: 'Ta aplikacja została wycofana. Jeśli potrzebujesz aplikacji społecznościowej, użyj Wix Groups.' },
  pt: { title: 'O Wix Forum não está mais disponível', description: 'Este aplicativo foi descontinuado. Se você precisa de um app de comunidade, use o Wix Groups.' },
  ro: { title: 'Wix Forum nu mai este disponibil', description: 'Această aplicație a fost întreruptă. Dacă aveți nevoie de o aplicație de comunitate, utilizați Wix Groups.' },
  ru: { title: 'Wix Forum больше не доступен', description: 'Это приложение было прекращено. Если вам нужно приложение для сообщества, используйте Wix Groups.' },
  sk: { title: 'Wix Forum už nie je k dispozícii', description: 'Táto aplikácia bola ukončená. Ak potrebujete komunitnú aplikáciu, použite Wix Groups.' },
  sl: { title: 'Wix Forum ni več na voljo', description: 'Ta aplikacija je bila ukinjena. Če potrebujete aplikacijo za skupnost, uporabite Wix Groups.' },
  sv: { title: 'Wix Forum är inte längre tillgängligt', description: 'Den här applikationen har avvecklats. Om du behöver en community-app, använd Wix Groups.' },
  th: { title: 'Wix Forum ไม่สามารถใช้งานได้อีกต่อไป', description: 'แอปพลิเคชันนี้ถูกยกเลิกแล้ว หากคุณต้องการแอปชุมชน ให้ใช้ Wix Groups' },
  tl: { title: 'Hindi na available ang Wix Forum', description: 'Ang application na ito ay hindi na ipinagpapatuloy. Kung kailangan mo ng community app, gamitin ang Wix Groups.' },
  tr: { title: 'Wix Forum artık kullanılamıyor', description: 'Bu uygulama kullanımdan kaldırılmıştır. Bir topluluk uygulamasına ihtiyacınız varsa Wix Groups\'u kullanın.' },
  uk: { title: 'Wix Forum більше не доступний', description: 'Цей додаток було припинено. Якщо вам потрібен додаток для спільноти, використовуйте Wix Groups.' },
  vi: { title: 'Wix Forum không còn khả dụng', description: 'Ứng dụng này đã ngừng hoạt động. Nếu bạn cần ứng dụng cộng đồng, hãy sử dụng Wix Groups.' },
  zh: { title: 'Wix Forum 已不再可用', description: '此应用程序已停止服务。如果您需要社区应用，请使用 Wix Groups。' },
};

const getDeprecationTranslations = (lang: string) =>
  DEPRECATION_TRANSLATIONS[lang] || DEPRECATION_TRANSLATIONS.en;

const DeprecatedEmptyState = ({ language }: { language?: string }) => {
  const t = getDeprecationTranslations(language || 'en');
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '100%', minHeight: '400px',
      fontFamily: 'Madefor, HelveticaNeue, Helvetica, Arial, sans-serif',
      padding: '40px 20px', boxSizing: 'border-box' as const,
      backgroundColor: '#fff',
    }}>
      <div style={{ textAlign: 'center', maxWidth: '500px' }}>
        <h2 style={{ color: '#333', fontSize: '24px', marginBottom: '12px' }}>
          {t.title}
        </h2>
        <p style={{ color: '#666', fontSize: '16px', lineHeight: '1.5' }}>
          {t.description}
        </p>
      </div>
    </div>
  );
};

const Widget = memo<WidgetProps<ControllerProps>>((props) => {
  if ((props as any).isDeprecated) {
    return <DeprecatedEmptyState language={(props as any).deprecatedLanguage} />;
  }

  const translations = getTranslations(props.state);
  const language = getBaseLanguage(props.state);
  const currentUser = getCurrentUser(props.state);
  const currentError = getCurrentError(props.state);

  const i18nICU = useMemo(
    () => getI18nConfigICU(language, translations),
    [language, translations],
  );

  return (
    <I18nextProviderICU i18n={i18nICU}>
      <PermissionsContextProvider user={currentUser}>
        <BiLoggerContextProvider logBiEvent={props.actions.logBiEvent}>
          <ErrorBoundary
            error={currentError}
            handleLogError={props.actions.logError}
          >
            <MemberComments {...props} />
          </ErrorBoundary>
        </BiLoggerContextProvider>
      </PermissionsContextProvider>
    </I18nextProviderICU>
  );
});

export default Widget;
